import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Plus, Trash2, BookOpen, GraduationCap, Layers } from 'lucide-react';
import { toast } from 'sonner';

const ManageSettings = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newBoard, setNewBoard] = useState('');
  const [newGrade, setNewGrade] = useState('');
  const [newSubject, setNewSubject] = useState('');
  
  const [addingBoard, setAddingBoard] = useState(false);
  const [addingGrade, setAddingGrade] = useState(false);
  const [addingSubject, setAddingSubject] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [boardsRes, gradesRes, subjectsRes] = await Promise.all([
        api.get('/boards'),
        api.get('/grades'),
        api.get('/subjects'),
      ]);
      setBoards(boardsRes.data);
      setGrades(gradesRes.data);
      setSubjects(subjectsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBoard = async (e) => {
    e.preventDefault();
    if (!newBoard.trim()) return;
    
    setAddingBoard(true);
    try {
      const res = await api.post('/boards', { name: newBoard.trim() });
      setBoards([...boards, res.data]);
      setNewBoard('');
      toast.success(`Board "${res.data.name}" added successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add board');
    } finally {
      setAddingBoard(false);
    }
  };

  const handleAddGrade = async (e) => {
    e.preventDefault();
    if (!newGrade.trim()) return;
    
    setAddingGrade(true);
    try {
      const res = await api.post('/grades', { name: newGrade.trim() });
      setGrades([...grades, res.data]);
      setNewGrade('');
      toast.success(`Grade "${res.data.name}" added successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add grade');
    } finally {
      setAddingGrade(false);
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    
    setAddingSubject(true);
    try {
      const res = await api.post('/subjects', { name: newSubject.trim() });
      setSubjects([...subjects, res.data]);
      setNewSubject('');
      toast.success(`Subject "${res.data.name}" added successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add subject');
    } finally {
      setAddingSubject(false);
    }
  };

  const handleDeleteBoard = async (boardId, boardName) => {
    if (!window.confirm(`Delete board "${boardName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/boards/${boardId}`);
      setBoards(boards.filter(b => b.id !== boardId));
      toast.success(`Board "${boardName}" deleted`);
    } catch (err) {
      toast.error('Failed to delete board');
    }
  };

  const handleDeleteGrade = async (gradeId, gradeName) => {
    if (!window.confirm(`Delete grade "${gradeName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/grades/${gradeId}`);
      setGrades(grades.filter(g => g.id !== gradeId));
      toast.success(`Grade "${gradeName}" deleted`);
    } catch (err) {
      toast.error('Failed to delete grade');
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    if (!window.confirm(`Delete subject "${subjectName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/subjects/${subjectId}`);
      setSubjects(subjects.filter(s => s.id !== subjectId));
      toast.success(`Subject "${subjectName}" deleted`);
    } catch (err) {
      toast.error('Failed to delete subject');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/teacher/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-700 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-slate-900">Manage Settings</h1>
          <p className="text-slate-600">Add and manage boards, grades, and subjects</p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Boards Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Layers className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Boards</h2>
                <p className="text-sm text-slate-500">{boards.length} boards</p>
              </div>
            </div>

            <form onSubmit={handleAddBoard} className="flex gap-2 mb-4">
              <input
                type="text"
                value={newBoard}
                onChange={(e) => setNewBoard(e.target.value)}
                placeholder="e.g., IGCSE, CBSE"
                className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
              <button
                type="submit"
                disabled={addingBoard || !newBoard.trim()}
                className="h-10 w-10 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
              </button>
            </form>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {boards.map(board => (
                <div
                  key={board.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="font-medium text-slate-700">{board.name}</span>
                  <button
                    onClick={() => handleDeleteBoard(board.id, board.name)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {boards.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No boards added yet</p>
              )}
            </div>
          </div>

          {/* Grades Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Grades</h2>
                <p className="text-sm text-slate-500">{grades.length} grades</p>
              </div>
            </div>

            <form onSubmit={handleAddGrade} className="flex gap-2 mb-4">
              <input
                type="text"
                value={newGrade}
                onChange={(e) => setNewGrade(e.target.value)}
                placeholder="e.g., Grade 8, Grade 9"
                className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
              <button
                type="submit"
                disabled={addingGrade || !newGrade.trim()}
                className="h-10 w-10 flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
              </button>
            </form>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {grades.map(grade => (
                <div
                  key={grade.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="font-medium text-slate-700">{grade.name}</span>
                  <button
                    onClick={() => handleDeleteGrade(grade.id, grade.name)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {grades.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No grades added yet</p>
              )}
            </div>
          </div>

          {/* Subjects Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Subjects</h2>
                <p className="text-sm text-slate-500">{subjects.length} subjects</p>
              </div>
            </div>

            <form onSubmit={handleAddSubject} className="flex gap-2 mb-4">
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="e.g., Physics, Chemistry"
                className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <button
                type="submit"
                disabled={addingSubject || !newSubject.trim()}
                className="h-10 w-10 flex items-center justify-center bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
              </button>
            </form>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {subjects.map(subject => (
                <div
                  key={subject.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="font-medium text-slate-700">{subject.name}</span>
                  <button
                    onClick={() => handleDeleteSubject(subject.id, subject.name)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {subjects.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No subjects added yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-xl p-6">
          <h3 className="font-semibold text-indigo-900 mb-2">How it works</h3>
          <ul className="text-sm text-indigo-800 space-y-1">
            <li>• Add boards (e.g., IGCSE, CBSE, SSC) that your school follows</li>
            <li>• Add grades (e.g., Grade 8, Grade 9, Grade 10) for different class levels</li>
            <li>• Add subjects (e.g., Physics, Chemistry, Biology) that you teach</li>
            <li>• When creating exams, you'll select the board, grade, and subject</li>
            <li>• Students will only see exams that match their profile</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageSettings;
